import { useState, useEffect, useCallback } from 'react';
import { useRoomContext, useLocalParticipant } from '@livekit/components-react';
import { useDispatch, useSelector } from 'react-redux';
import { updateParticipantMetadata } from '../redux/meeting/actions';

const RAISE_HAND_EVENT = 'RAISE_HAND';
const LOWER_HAND_EVENT = 'LOWER_HAND';

export const useRaiseHand = () => {
  const room = useRoomContext();
  const { localParticipant } = useLocalParticipant();
  const [isHandRaised, setIsHandRaised] = useState(false);
  const dispatch = useDispatch();
  const meetingId = useSelector(state => state.meeting.meetingId);

  // Sync state with metadata (source of truth)
  useEffect(() => {
    if (!localParticipant) return;
    try {
        const metadata = localParticipant.metadata ? JSON.parse(localParticipant.metadata) : {};
        if (metadata.handRaised !== undefined) {
             setIsHandRaised(!!metadata.handRaised);
        }
    } catch (e) {
        // console.warn('Failed to parse metadata', e);
    }
  }, [localParticipant, localParticipant?.metadata]);

  const updateHandState = useCallback(async (raised) => {
    if (!localParticipant) return;
    
    // Optimistic Update
    setIsHandRaised(raised);
    
    let metadata = {};
    try {
        metadata = localParticipant.metadata ? JSON.parse(localParticipant.metadata) : {};
    } catch (e) {
        // console.error("Error parsing metadata", e);
    }
    
    const newMetadata = { ...metadata, handRaised: raised };
    
    try {
        // Call Backend API
        dispatch(updateParticipantMetadata(meetingId, localParticipant.identity, JSON.stringify(newMetadata)));
        // console.log('Metadata update requested via backend:', newMetadata);

        // Keep DataPacket for immediate UI feedback if desired, or rely on metadata optimized update
        const payload = JSON.stringify({
            type: raised ? RAISE_HAND_EVENT : LOWER_HAND_EVENT,
            participantId: localParticipant.identity,
            handRaised: raised,
            timestamp: Date.now()
        });
        
        const encoder = new TextEncoder();
        const data = encoder.encode(payload);
        
        await localParticipant.publishData(data, { reliable: true });
    } catch (error) {
        // console.error("Failed to update hand state", error);
        // setIsHandRaised(!raised); // Revert optimistic if needed
    }
  }, [localParticipant, dispatch, meetingId]);

  const toggleRaiseHand = useCallback(() => {
    updateHandState(!isHandRaised);
  }, [isHandRaised, updateHandState]);

  const lowerHand = useCallback(() => {
      updateHandState(false);
  }, [updateHandState]);

  // Listen for remote lower hand requests (Moderator action)
  useEffect(() => {
      if (!room) return;

      const onDataReceived = (payload) => {
          const decoder = new TextDecoder();
          const strData = decoder.decode(payload);
          try {
              const data = JSON.parse(strData);
              if (data.type === LOWER_HAND_EVENT && data.participantId === localParticipant?.identity) {
                  // Moderator requested to lower my hand
                  updateHandState(false);
              }
          } catch (e) {
              // ignore
          }
      };

      room.on('data_received', onDataReceived);
      
      return () => {
          room.off('data_received', onDataReceived);
      };
  }, [room, localParticipant, updateHandState]);

  return {
    isHandRaised,
    toggleRaiseHand,
    lowerHand
  };
};
